import { QueryErrorResetBoundary } from '@mint-lib/error-boundary';
import { useTranslation } from '@mint-lib/i18n';
import { Tabs, TabsList, TabsTrigger } from '@mint-lib/tw-components';
import { Navigate, Link as RouterLink } from '@mint/routing';

import { AutoImportInfoButton } from '../components/molecules/AutoImportInfoButton.jsx';
import { ImportButton } from '../components/molecules/ImportButton.jsx';
import { ImportsInfoAlert } from '../components/molecules/ImportsInfoAlert.jsx';
import { PageHeader } from '../components/molecules/PageHeader.jsx';
import GlossaryTab from '../components/organisms/GlossaryTab.jsx';
import PastImportsTab from '../components/organisms/PastImportsTab.jsx';
import TemplatesTab from '../components/organisms/TemplatesTab.jsx';
import { TABS } from '../constants/importHubTabs.js';
import {
  useImportHubPaths,
  usePerformanceRouteApi,
} from '../hooks/useImportHubRouteApi.js';

const TabComponents = {
  [TABS.imports]: PastImportsTab,
  [TABS.templates]: TemplatesTab,
  [TABS.glossary]: GlossaryTab,
};

export default function PerformanceImports() {
  const { t } = useTranslation('@myn/import-hub');

  const { tab } = usePerformanceRouteApi().useSearch();
  const paths = useImportHubPaths();

  const CurrentTab = TabComponents[tab];

  if (!CurrentTab) {
    return <Navigate to={paths.performance} search={{ tab: 'imports' }} />;
  }

  return (
    <>
      <PageHeader
        title={t('Performance data import')}
        description={t(
          'Import performance data files from external sources and map them with platform items.',
        )}
        backLink
      >
        <div className="flex gap-2">
          <AutoImportInfoButton />
          <ImportButton />
        </div>
      </PageHeader>
      {tab !== 'glossary' && <ImportsInfoAlert />}
      <Tabs orientation="horizontal" value={tab} className="mb-4 w-full">
        <TabsList className="w-full">
          <TabsTrigger
            asChild
            value={TABS.imports}
            data-dd-action-name="Past imports tab"
          >
            <RouterLink
              to={paths.performance}
              search={{ tab: TABS.imports }}
              preload="intent"
            >
              {t('Past imports')}
            </RouterLink>
          </TabsTrigger>
          <TabsTrigger
            asChild
            value={TABS.templates}
            data-dd-action-name="Templates tab"
          >
            <RouterLink
              to={paths.performance}
              search={{ tab: TABS.templates }}
              preload="intent"
            >
              {t('Templates')}
            </RouterLink>
          </TabsTrigger>
          <TabsTrigger
            asChild
            value={TABS.glossary}
            data-dd-action-name="Glossary tab"
          >
            <RouterLink
              to={paths.performance}
              search={{ tab: TABS.glossary }}
              preload="intent"
            >
              {t('Glossary')}
            </RouterLink>
          </TabsTrigger>
        </TabsList>
      </Tabs>
      <QueryErrorResetBoundary>
        <CurrentTab />
      </QueryErrorResetBoundary>
    </>
  );
}
